import { getLocalTimeZone, today } from "@internationalized/date";
import { UseQueryResult } from "@tanstack/react-query";
import { DateTimeRangePicker } from "features/CenterContent/RoleContent/ProductionDeviation/components/DateTimeRangePicker";
import { ShowTrainSeries } from "features/CenterContent/RoleContent/ProductionDeviation/components/ShowTrainSeries";
import { DerivedTrainSeries } from "features/CenterContent/RoleContent/ProductionDeviation/steps/DerivedTrainSeries";
import { ProductionDeviationForm } from "features/CenterContent/RoleContent/ProductionDeviation/types/formSchema";
import {
  makeBatchProps,
  Path,
  TEMPLATE_TRAIN_SERIES_KEY,
  useProductionDeviationPath,
} from "features/CenterContent/RoleContent/ProductionDeviation/utils";
import { OperationalInformationTypes } from "features/CenterContent/shared/operationalInformation/hooks/useOperationalInformationTypes";
import { FormInputs } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/batchSections/FormInputs";
import { Dispatch, FC, SetStateAction, useEffect } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ActionModal } from "shared/components/ActionModal";
import { FailureMessage } from "shared/components/feedback/FailureMessage/FailureMessage";
import SkeletonLoader from "shared/components/feedback/SkeletonLoader/SkeletonLoader";
import { DropsStaticAlert, Flex, Text } from "shared/components/Spor";
import { useTheme } from "styled-components";

type ProductionDeviationFormInputsProps = {
  path: Path;
  isEditMode: boolean;
  onClose: () => void;
  onSubmit: () => void;
  opInfoTypes: UseQueryResult<OperationalInformationTypes>;
  trainSeries: string[];
  setTrainSeries: Dispatch<SetStateAction<string[]>>;
  trigger: () => Promise<boolean>;
};

export const ProductionDeviationFormInputs: FC<
  ProductionDeviationFormInputsProps
> = ({
  path,
  isEditMode,
  onClose,
  onSubmit,
  opInfoTypes,
  trainSeries,
  setTrainSeries,
  trigger,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { data, status } = opInfoTypes;
  const { control, setValue } = useFormContext<ProductionDeviationForm>();
  const hasTrainSeries = trainSeries.length > 0;

  // In create mode, Hendelse/Årsak/Handling are filled in via a template that
  // exists before any train serie has been picked; its values are then
  // propagated to every selected serie. In edit mode there is always exactly
  // one real serie, so it's edited directly.
  const { path: templatePath } = useProductionDeviationPath(
    TEMPLATE_TRAIN_SERIES_KEY,
  );
  const effectivePath = isEditMode ? path : templatePath;

  const batchTrainForm = useWatch({
    name: effectivePath("batchTrainForm"),
    control,
  });

  /**
   * Propagate the shared event/reason/action to the selected trainSeries
   */
  useEffect(() => {
    if (!batchTrainForm) return;
    const targetTrainSeries = isEditMode ? trainSeries.slice(1) : trainSeries;
    targetTrainSeries.forEach((trainSerie) => {
      const { path: otherTrainSeriePath } =
        useProductionDeviationPath(trainSerie);
      const directionsIndexes = [0, 1];
      directionsIndexes.forEach((i) => {
        const field = otherTrainSeriePath(`batchTrainForm.${i}.trainForm`);
        setValue(field, batchTrainForm[i].trainForm);
      });
    });
  }, [batchTrainForm]);

  const mode = isEditMode ? "edit" : "create";

  return (
    <ActionModal
      title={`${isEditMode ? t("Endre") : t("Opprett")} ${t("produksjonsavvik")}`}
      actionTitle={`${t("Neste")} →`}
      onClose={onClose}
      isSubmitDisabled={!isEditMode && !hasTrainSeries}
      onSubmit={async () => {
        const success = await trigger();
        if (!success) return;
        onSubmit();
      }}
      loading={false}
      isSuccess={false}
      isError={false}
      successMessage=""
      failureMessage=""
      subHeaders={
        isEditMode ? [<ShowTrainSeries trainSeries={trainSeries} />] : undefined
      }
    >
      <Flex flexDir="column" gap="6px" w="100%" mb="12px">
        {isEditMode && (
          <DropsStaticAlert variant="info" width="100%">
            {t("Opprett nytt avvik for å endre periode eller hendelsetype.")}
          </DropsStaticAlert>
        )}
        <Flex flexDir="column" gap="6px">
          <Text
            variant="sm"
            fontWeight={700}
            marginTop={isEditMode ? "9px" : undefined}
            color={isEditMode ? theme.colorTextDisabled : theme.colorTextMain}
          >
            {t("Tidsperiode")}
          </Text>
          <DateTimeRangePicker
            durationsField="durations"
            minValue={isEditMode ? undefined : today(getLocalTimeZone())}
            maxValue={
              isEditMode
                ? undefined
                : today(getLocalTimeZone()).add({ days: 13 })
            }
            isDisabled={isEditMode}
          />
        </Flex>
      </Flex>
      {!isEditMode && (
        <Flex w="100%" mb="12px">
          <DerivedTrainSeries
            trainSeries={trainSeries}
            setTrainSeries={setTrainSeries}
          />
        </Flex>
      )}
      <Flex w="100%">
        {status === "pending" && (
          <SkeletonLoader skeletonType="trainInfoForm" />
        )}
        {status === "error" && <FailureMessage flex={1} m="0px" maxW="100%" />}
        {status === "success" && (
          <FormInputs {...makeBatchProps(effectivePath, data, mode)} />
        )}
      </Flex>
    </ActionModal>
  );
};
